import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Select, message } from "antd";

import { all_routes } from "../router/all_routes";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import CommentReplyModal from "./CommentReplyModal";

import { fetchAllTickets, fetchTicketById, fetchTicketsByDepartment, fetchTicketsByEmployeeHierarchy, fetchTicketsSummaryByDepartment, fetchTicketSummary, updateTicket } from "../../core/data/redux/tickets/ticket/ticketActions";
import { fetchTicketDetailsWithReplies, updateTicketAssignee, updateTicketPriority } from "../../core/data/redux/tickets/ticketDetail/ticketDetailActions";
import { getEmployees } from "../../core/data/redux/employee/employeeAction";
import { fetchTicketCategorySummaryByDepartment } from "../../core/data/redux/tickets/ticketCategoryActions";
import { filterByStatus } from "../../core/utils/filterActiveData";
import { AppDispatch } from "../../core/data/redux/store";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";

const TicketDetails = () => {
    const routes = all_routes;
    const dispatch = useDispatch<AppDispatch>();
    const { ticket_id } = useParams<{ ticket_id: string }>();

    const { selectedTicket } = useSelector((state: any) => state.ticket.createTickit);
    const { ticketRepleisComments } = useSelector((state: any) => state.ticket.ticketDetail);
    const { employees } = useSelector((state: any) => state.employee);
    const { employeesbyUser } = useSelector((state: any) => state.employee);
    const { user } = useSelector((state: any) => state.auth);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [parentId, setParentId] = useState<number | null>(null);
    const [status, setStatus] = useState<string>(selectedTicket?.currentStatus || 'Open');

    const activeEmployeeData = useMemo(() => filterByStatus(employees, 'active', 'employee_status'), [employees]);

    const filteredAssignTo = useMemo(() => {
        if (!selectedTicket?.DepartmentId) return activeEmployeeData;
        return activeEmployeeData.filter(
            (emp: any) => emp?.DepartmentId === selectedTicket?.DepartmentId
        );
    }, [selectedTicket?.DepartmentId, activeEmployeeData]);

    useEffect(() => {
        if (!employees.length) dispatch(getEmployees());
    }, [employees, dispatch]);

    useEffect(() => {
        if (ticket_id) {
            dispatch(fetchTicketById(ticket_id));
            dispatch(fetchTicketDetailsWithReplies(ticket_id));
        }
    }, [dispatch, ticket_id]);

    useEffect(() => {
        if (selectedTicket?.ticket_status) {
            setStatus(selectedTicket.ticket_status);
        }
    }, [selectedTicket?.ticket_status]);

    const refreshTicketData = async () => {
        if (!ticket_id) return;
        await Promise.all([
            dispatch(fetchTicketById(ticket_id)),
            dispatch(fetchAllTickets()),
            dispatch(fetchTicketSummary()),
            dispatch(fetchTicketsByDepartment(selectedTicket.DepartmentId)),
            dispatch(fetchTicketsSummaryByDepartment(selectedTicket.DepartmentId)),
            dispatch(fetchTicketCategorySummaryByDepartment(selectedTicket.DepartmentId)),
            dispatch(fetchTicketsByEmployeeHierarchy({
                employeeId: employeesbyUser.employeeId,
                departmentId: employeesbyUser.DepartmentId
            }))
        ]);
    };

    const handleStatusChange = (newStatus: string) => {
        const statusLabel = ticketStatus.find(s => s.value === newStatus)?.label || newStatus;

        Modal.confirm({
            title: 'Change Ticket Status?',
            content: `Are you sure you want to change the status to "${statusLabel}"?`,
            onOk: async () => {
                setStatus(newStatus);
                try {
                    await dispatch(updateTicket({
                        ticketId: Number(ticket_id),
                        ticket_status: newStatus,
                        ModifiedBy: user.UserId,
                    }));
                    await refreshTicketData();
                    message.success(`Status changed to ${statusLabel}`);
                } catch (error) {
                    console.error("Failed to update ticket status", error);
                    message.error("Failed to update ticket status");
                }
            },
        });
    };

    const handlePriorityChange = (value: string) => {
        const label = value === '0' ? 'High' : value === '1' ? 'Medium' : 'Low';
        Modal.confirm({
            title: 'Change Priority?',
            content: `Are you sure you want to set priority to ${label}?`,
            onOk: async () => {
                try {
                    await dispatch(updateTicketPriority({ ticketId: ticket_id, priority: value })).unwrap();
                    await refreshTicketData();
                    message.success("Priority updated successfully");
                } catch (err) {
                    message.error("Failed to update priority");
                }
            },
        });
    };

    const handleAssignToChange = (value: number) => {
        const selectedEmp = filteredAssignTo.find(emp => emp.EmployeeID === value);
        const name = selectedEmp ? `${selectedEmp.FirstName} ${selectedEmp.LastName}` : 'this employee';
        Modal.confirm({
            title: 'Change Assignee?',
            content: `Are you sure you want to assign this ticket to ${name}?`,
            onOk: async () => {
                try {
                    await dispatch(updateTicketAssignee({
                        ticketId: ticket_id,
                        assignedTo: value,
                        modifiedBy: user.UserId,
                    })).unwrap();
                    await refreshTicketData();
                    message.success("Assignee updated successfully");
                } catch (err) {
                    message.error("Failed to update assignee");
                }
            },
        });
    };

    const handleOpenModal = (parentId: number | null = null) => {
        setParentId(parentId);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setParentId(null);
    };

    const ticketStatus = [
        { value: "Open", label: "Open" },
        { value: "Pending", label: "Pending" },
        { value: "Solved", label: "Solved" },
        { value: "Discussion", label: "Discussion" },
        { value: "Development", label: "Development" },
        { value: "Fixed", label: "Fixed" },
        { value: "Testing", label: "Testing" },
        { value: "Closed", label: "Closed" },
    ];

    const CommentThread = ({ comment, handleOpenModal }: { comment: any; handleOpenModal: (id: number) => void }) => (
        <div className="mb-3 border-start ps-3">
            <div className="d-flex mb-2">
                <img
                    src={comment?.PictureURL ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                    alt="Avatar"
                    className="rounded-circle me-2"
                    width="40"
                    height="40"
                />
                <div>
                    <h6 className="mb-0 fw-medium">{comment?.Name || "Anonymous"}</h6>
                    <small className="text-muted">
                        <i className="ti ti-calendar-bolt me-1" />
                        <span className="me-1">Updated</span>
                        {new Date(comment?.ModifiedAt).toLocaleString()}
                    </small>
                </div>
            </div>
            <div className="bg-white rounded p-3 border">
                <p className="mb-0">{comment?.Response}</p>
            </div>
            {(!comment?.Replies || comment.Replies.length === 0) && (
                <button onClick={() => handleOpenModal(comment.Id)}
                    className="btn btn-sm btn-outline-secondary mt-2"
                >
                    <i className="ti ti-arrow-forward-up me-1" />
                    Reply
                </button>
            )}
            {comment?.Replies?.length > 0 && (
                <div className="mt-3">
                    {comment.Replies.map((nestedReply: any) => (
                        <CommentThread key={nestedReply.Id} comment={nestedReply} handleOpenModal={handleOpenModal} />
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <>
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="mb-2">
                            <h6 className="fw-medium d-flex align-items-center">
                                <Link to={routes.tickets}>
                                    <i className="ti ti-arrow-left me-2" />
                                    Ticket Details
                                </Link>
                            </h6>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                            <div className="head-icons ms-2">
                                <CollapseHeader />
                            </div>
                        </div>
                    </div>
                    {/* /Breadcrumb */}
                    <div className="row">
                        <div className="col-xl-9 col-md-8">
                            {/* Ticket Main Info */}
                            <div className="card">
                                <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                                    <h5 className="text-info fw-medium">{selectedTicket?.department_name}</h5>
                                    <div className="d-flex align-items-center">
                                        <span className="badge bg-danger me-3">
                                            <i className="ti ti-circle-filled fs-5 me-1" />
                                            {selectedTicket?.priority == 0 ? "High" : selectedTicket?.priority == 1 ? "Medium" : selectedTicket?.priority == 2 ? "Low" : "Unknown"}
                                        </span>
                                        <div>
                                            {selectedTicket?.doc_path && (
                                                (() => {
                                                    const fileExtension = selectedTicket?.doc_path.split('.').pop()?.toLowerCase();
                                                    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileExtension);
                                                    if (isImage) {
                                                        return (
                                                            <button
                                                                className="btn btn-sm btn-outline-secondary me-2"
                                                                onClick={() => window.open(selectedTicket?.doc_path, "_blank")}
                                                                title="View Image"
                                                            >
                                                                <EyeOutlined />
                                                            </button>
                                                        );
                                                    } else {
                                                        return (
                                                            <button
                                                                className="btn btn-sm btn-outline-success me-2"
                                                                onClick={() => window.location.href = selectedTicket?.doc_path}
                                                                title="Download File"
                                                            >
                                                                <DownloadOutlined />
                                                            </button>
                                                        );
                                                    }
                                                })()
                                            )}
                                        </div>
                                    </div>

                                </div>
                                <div className="card-body">
                                    {/* Ticket Header */}
                                    <div className="d-flex align-items-center justify-content-between flex-wrap border-bottom mb-3">
                                        <div className="mb-3">
                                            <span className="badge badge-info rounded-pill mb-2">
                                                {selectedTicket?.ticket_number}
                                            </span>
                                            <div className="d-flex align-items-center mb-2">
                                                <h5 className="fw-semibold me-2">{selectedTicket?.title}</h5>
                                                <span className="badge bg-outline-pink d-flex align-items-center ms-1">
                                                    <i className="ti ti-circle-filled fs-5 me-1" />
                                                    {selectedTicket?.ticket_status}
                                                </span>
                                            </div>
                                            <div className="d-flex align-items-center flex-wrap row-gap-2">
                                                <p className="d-flex align-items-center mb-0 me-2">
                                                    <span className="avatar avatar-md avatar-rounded border border-2 border-white me-2">
                                                        <img
                                                            src={selectedTicket?.assigned_to_img || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                                                            alt="Profile"
                                                            className="img-fluid rounded-circle"
                                                        />
                                                    </span>
                                                    {selectedTicket?.assigned_to_name && selectedTicket?.assigned_to_name !== 'Unassigned'
                                                        ? <span>Assigned to <span className="text-dark ms-1">{selectedTicket?.assigned_to_name}</span></span>
                                                        : <span className="text-danger fw-bold">Unassigned</span>}
                                                </p>
                                                <p className="d-flex align-items-center mb-0 me-2">
                                                    <i className="ti ti-calendar-bolt me-1" />
                                                    Updated {new Date(selectedTicket?.ModifiedAt).toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="mb-3">

                                            <button className="btn btn-primary" onClick={() => handleOpenModal(null)}>
                                                <i className="ti ti-arrow-forward-up me-1" />
                                                Post a Reply
                                            </button>
                                        </div>
                                    </div>

                                    {/* Ticket Comments */}
                                    <div className="border-bottom mb-3 pb-3">
                                        <div className="mt-4">
                                            {ticketRepleisComments?.map((comment: any) => (
                                                <div key={comment.Id} className="mb-4 border rounded p-3 bg-light">
                                                    <div className="d-flex mb-2">
                                                        <img
                                                            src={comment?.PictureURL || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                                                            alt="Avatar"
                                                            className="rounded-circle me-2"
                                                            width="45"
                                                            height="45"
                                                        />
                                                        <div>
                                                            <h6 className="mb-0 fw-semibold">{comment?.Name || "Anonymous"}</h6>
                                                            <small className="text-muted">
                                                                <i className="ti ti-calendar-bolt me-1" />
                                                                <span className="me-1">Updated</span>
                                                                {new Date(comment?.ModifiedAt).toLocaleString()}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <div className="ps-1">
                                                        <div className="bg-white rounded shadow-sm p-3">
                                                            <p className="mb-0">{comment?.Response}</p>
                                                        </div>
                                                        {comment?.Replies?.length === 0 && (
                                                            <button
                                                                onClick={() => handleOpenModal(comment.Id)}
                                                                className="btn btn-sm btn-outline-primary mt-2"
                                                            >
                                                                <i className="ti ti-arrow-forward-up me-1" />
                                                                Reply
                                                            </button>
                                                        )}
                                                    </div>
                                                    {comment?.Replies?.length > 0 && (
                                                        <div className="mt-3 border-start">
                                                            {comment.Replies.map((reply: any) => (
                                                                <CommentThread key={reply.Id} comment={reply} handleOpenModal={handleOpenModal} />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Ticket Details Sidebar */}
                        <div className="col-xl-3 col-md-4">
                            <div className="card">
                                <div className="card-header p-3">
                                    <h4>Ticket Details</h4>
                                </div>
                                <div className="card-body p-0">
                                    <div className="border-bottom p-3">
                                        <div className="mb-3">
                                            <label className="form-label">Priority</label>
                                            <Select
                                                style={{ width: '100%' }}
                                                value={selectedTicket?.priority?.toString() ?? null}
                                                onChange={handlePriorityChange}
                                                options={[
                                                    { value: '0', label: 'High' },
                                                    { value: '1', label: 'Medium' },
                                                    { value: '2', label: 'Low' },
                                                ]}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Assign To</label>
                                            <Select
                                                style={{ width: '100%' }}
                                                value={selectedTicket?.AssignedTo || null}
                                                onChange={handleAssignToChange}
                                                showSearch
                                                placeholder="Select an employee"
                                                optionFilterProp="children"
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                            >
                                                {filteredAssignTo.map((emp: any) => (
                                                    <Select.Option
                                                        key={emp.EmployeeID}
                                                        value={emp.EmployeeID}
                                                        label={`${emp.FirstName} ${emp.LastName}`}
                                                    >
                                                        <span className="d-flex align-items-center">
                                                            <img
                                                                src={emp.ProfileImage || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                                                                alt="avatar"
                                                                className="me-2 rounded-circle"
                                                                style={{ width: 24, height: 24, objectFit: "cover" }}
                                                            />
                                                            {emp.FirstName} {emp.LastName}
                                                        </span>
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </div>
                                        <div>
                                            <label className="form-label">Ticket Status</label>
                                            <Select
                                                style={{ width: '100%' }}
                                                options={ticketStatus}
                                                value={status}
                                                onChange={handleStatusChange}
                                                dropdownRender={menu => (
                                                    <div style={{
                                                        backgroundColor: "#fff",
                                                        border: '1px solid #d9d9d9',
                                                        borderRadius: '4px',
                                                        boxShadow: '0 3px 6px rgba(0,0,0,0.1)'
                                                    }}>
                                                        {menu}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By{" "}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>

            {isModalOpen && <div className="modal-backdrop fade show" />}

            <CommentReplyModal
                isOpen={isModalOpen}
                closeModal={handleCloseModal}
                ticketId={ticket_id}
                parentId={parentId}
            />
        </>
    );
};

export default TicketDetails;
